/**
 * FANTASY CONTEST DETAIL PAGE
 * 
 * View contest details, pick your roster, and enter a fantasy contest.
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ArrowLeft,
  Trophy,
  Users,
  Clock,
  DollarSign,
  Loader,
  CheckCircle,
  AlertCircle,
  Search,
  Filter,
  X,
  ChevronDown,
  ChevronUp,
  Star,
  Zap,
  Lock,
  UserPlus,
  Trash2,
  RefreshCw
} from 'lucide-react'
import toast from 'react-hot-toast'
import axios from 'axios'
import { API_BASE_URL } from '../constants'
import { useAuthStore } from '../store/authStore'
import PlayerInsightsModal from '../components/PlayerInsightsModal'
import TeamInsightsModal from '../components/TeamInsightsModal'
import FantasyChat from '../components/FantasyChat'

// ============================================================================
// TYPES
// ============================================================================

interface Player {
  id: string
  name: string
  team: string
  position: string
  salary: number
  tier?: number
  projectedPoints?: number
  opponent?: string
  gameTime?: string
  byeWeek?: boolean
  injury?: string
}

interface GameWindow {
  gameNumber: number
  description: string
  lockTime: string
  unlockTime: string | null
  gameStartTime: string
  gameEndTime: string
}

interface FantasyContest {
  id: number
  title: string
  description: string
  sport: string
  contest_type: string
  contest_category?: 'dfs' | 'survivor'  // DFS/roster or survivor/pick'em
  week?: number
  season: string
  rosterRules: {
    // Type: 'dfs' for player roster, 'survivor' for team picks
    type?: 'dfs' | 'survivor'
    // Supports both old format (number) and new agentic format (object with eligiblePositions)
    positions?: Record<string, number | {
      count: number
      eligiblePositions?: string[]
      multiplier?: number
      description?: string
    }>
    // Survivor-specific fields
    picksRequired?: number
    pickFormat?: string
    winCondition?: string
    contestRules?: string
    // DFS-specific fields
    salaryCap?: number
    maxPlayersPerTeam?: number
  }
  scoringSystem: any
  prizeStructure: any
  playerPool: Player[]
  roster_lock_time: string
  first_game_time?: string
  last_game_time?: string
  resolution_time: string
  entry_fee: number
  max_entries: number
  current_entries?: number
  entryCount?: number
  prize_pool?: number
  status: string
  creator_name?: string
  rolling_lock?: number
  game_windows?: GameWindow[]
  lock_timing_choice?: string
  created_at?: string
  // Swap rules for rolling lock contests
  swap_rules?: {
    max_swaps_per_window: number | null
    max_swaps_total: number | null
    swap_type: 'any' | 'worst_only' | 'locked_captain'
    swap_cost_points: number
    no_repeat_swaps: boolean
  }
  // Full contest plan details
  games_data?: Array<{
    homeTeam: string
    awayTeam: string
    gameDate: string
    gameTime?: string
    venue?: string
  }>
  teams?: string[]
}

interface RosterSlot {
  position: string
  player: Player | null
  slotIndex: number
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function FantasyContestDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  
  // Contest data
  const [contest, setContest] = useState<FantasyContest | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  
  // Roster state (for DFS contests)
  const [roster, setRoster] = useState<Record<string, string>>({})
  const [expandedPosition, setExpandedPosition] = useState<string | null>(null)
  
  // Survivor picks state (for survivor/pick'em contests)
  // Key: gameId, Value: picked team name
  const [survivorPicks, setSurvivorPicks] = useState<Record<string, string>>({})
  // Captain pick (for captain format - 2x multiplier)
  const [captainPick, setCaptainPick] = useState<string | null>(null) // gameId of captain pick
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [positionFilter, setPositionFilter] = useState<string>('ALL')
  const [sortBy, setSortBy] = useState<'salary' | 'projected' | 'name'>('salary')
  
  // User's entries (multi-entry support)
  const [myEntries, setMyEntries] = useState<any[]>([])
  const [currentEntryId, setCurrentEntryId] = useState<number | null>(null)
  const [entryName, setEntryName] = useState('')
  const [maxEntries, setMaxEntries] = useState(1)
  const [existingEntry, setExistingEntry] = useState<any>(null)
  
  // Leaderboard
  const [leaderboard, setLeaderboard] = useState<any[]>([])
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false)
  
  // Tab state
  const [activeTab, setActiveTab] = useState<'roster' | 'leaderboard' | 'scores' | 'rules'>('roster')
  
  // Score tracking
  const [lastScoreUpdate, setLastScoreUpdate] = useState<Date | null>(null)
  const [isRefreshingScores, setIsRefreshingScores] = useState(false)
  
  // Player insights modal
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null)
  const [showPlayerModal, setShowPlayerModal] = useState(false)
  
  // Team insights modal (for survivor contests)
  const [selectedTeam, setSelectedTeam] = useState<{ name: string; opponent?: string; isHome?: boolean } | null>(null)
  const [selectedGame, setSelectedGame] = useState<any>(null)
  const [showTeamModal, setShowTeamModal] = useState(false)
  
  // Load contest on mount
  useEffect(() => {
    if (id) {
      loadContest()
      loadLeaderboard()
      if (user?.id) {
        loadExistingEntry()
      }
    }
  }, [id, user?.id])
  
  const loadContest = async () => {
    try {
      setLoading(true)
      const response = await axios.get(`${API_BASE_URL}/api/fantasy/contests/${id}`)
      setContest(response.data.contest)
    } catch (error) {
      console.error('Failed to load contest:', error)
      toast.error('Failed to load contest')
    } finally {
      setLoading(false)
    }
  }
  
  const loadExistingEntry = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/fantasy/contests/${id}/my-entries?userId=${user?.id}`
      )
      if (response.data.entries && response.data.entries.length > 0) {
        setMyEntries(response.data.entries)
        setMaxEntries(response.data.maxEntries || 1)
        
        // Load the first entry by default (or continue editing current one)
        const entryToLoad = currentEntryId 
          ? response.data.entries.find((e: any) => e.id === currentEntryId) || response.data.entries[0]
          : response.data.entries[0]
        
        setExistingEntry(entryToLoad)
        setCurrentEntryId(entryToLoad.id)
        setEntryName(entryToLoad.entry_name || '')
        setRoster(entryToLoad.roster || {})
      } else {
        setMaxEntries(response.data.maxEntries || 1)
      }
    } catch (error) {
      console.error('Failed to load existing entries:', error)
    }
  }
  
  // Switch to editing a different entry
  const switchEntry = (entry: any) => {
    setCurrentEntryId(entry.id)
    setExistingEntry(entry)
    setEntryName(entry.entry_name || '')
    setRoster(entry.roster || {})
    toast.success(`Editing ${entry.entry_name || 'Entry #' + entry.entry_number}`)
  }
  
  // Start a new entry (for multi-entry)
  const startNewEntry = () => {
    if (myEntries.length >= maxEntries) {
      toast.error(`You've reached the maximum of ${maxEntries} entries`)
      return
    }
    setCurrentEntryId(null)
    setExistingEntry(null)
    setEntryName(`Entry #${myEntries.length + 1}`)
    setRoster({})
    toast.success('Starting new entry!')
  }
  
  const loadLeaderboard = async () => {
    try {
      setLoadingLeaderboard(true)
      const response = await axios.get(`${API_BASE_URL}/api/fantasy/contests/${id}/leaderboard`)
      setLeaderboard(response.data.leaderboard || [])
      setLastScoreUpdate(new Date())
    } catch (error) {
      console.error('Failed to load leaderboard:', error)
      setLeaderboard([])
    } finally {
      setLoadingLeaderboard(false)
    }
  }
  
  const refreshScores = async () => {
    try {
      setIsRefreshingScores(true)
      // Trigger server-side score refresh
      await axios.post(`${API_BASE_URL}/api/fantasy/contests/${id}/refresh-scores`)
      // Reload data
      await Promise.all([loadLeaderboard(), loadExistingEntry()])
      toast.success('Scores refreshed!')
    } catch (error) {
      console.error('Failed to refresh scores:', error)
      toast.error('Failed to refresh scores')
    } finally {
      setIsRefreshingScores(false)
    }
  }
  
  // Get player scores from existing entry
  const getPlayerScore = (playerId: string): { points: number; breakdown: Record<string, number> } | null => {
    if (!existingEntry?.playerScores) return null
    const playerScore = existingEntry.playerScores.find((p: any) => p.playerId === playerId)
    if (!playerScore) return null
    return {
      points: playerScore.fantasyPoints || 0,
      breakdown: playerScore.pointBreakdown || {}
    }
  }
  
  // ============================================================================
  // ROSTER LOGIC
  // ============================================================================

  // Helper function to find player by ID - memoized to prevent unnecessary rerenders
  const findPlayerById = useCallback((playerId: string): Player | null => {
    if (!contest?.playerPool || !playerId) return null
    return contest.playerPool.find(p => p.id === playerId) || null
  }, [contest?.playerPool])

  // 🎯 Check if this is a survivor/team contest (not DFS)
  const isSurvivorContest = contest?.contest_category === 'survivor' || contest?.rosterRules?.type === 'survivor'
  
  // Build roster slots from contest rules (DFS only - survivor uses team picker)
  const rosterSlots = useMemo(() => {
    // Skip for survivor contests - they use team picker, not player roster
    if (isSurvivorContest) {
      console.log('🎯 Survivor contest - skipping roster slots (using team picker instead)')
      return []
    }
    
    if (!contest?.rosterRules?.positions) {
      console.log('⚠️ No roster rules or positions found')
      return []
    }
    
    const slots: RosterSlot[] = []
    const positions = contest.rosterRules.positions
    
    console.log('📋 Building roster slots from positions:', positions)
    console.log('📋 Current roster state:', roster)
    
    Object.entries(positions).forEach(([position, posData]) => {
      // Handle both formats: simple number OR complex object {count, multiplier, description, eligiblePositions}
      const count = typeof posData === 'object' && posData !== null 
        ? (posData as any).count || 1 
        : posData as number;
      
      for (let i = 0; i < count; i++) {
        const slotKey = count > 1 ? `${position}${i + 1}` : position
        const playerId = roster[slotKey]
        const player = playerId ? findPlayerById(playerId) : null
        
        slots.push({
          position,
          player,
          slotIndex: i
        })
      }
    })
    
    console.log('📋 Generated', slots.length, 'roster slots:', slots.map(s => `${s.position}${s.slotIndex + 1}: ${s.player?.name || 'empty'}`))
    return slots
  }, [contest?.rosterRules?.positions, roster, findPlayerById, isSurvivorContest])
  
  // Calculate salary used
  const salaryUsed = useMemo(() => {
    return Object.values(roster).reduce((total, playerId) => {
      const player = findPlayerById(playerId)
      return total + (player?.salary || 0)
    }, 0)
  }, [roster, contest])
  
  const salaryCap = contest?.rosterRules?.salaryCap || 50000
  const salaryRemaining = salaryCap - salaryUsed
  
  // Check if roster/picks are complete
  const isRosterComplete = useMemo(() => {
    // For survivor: check if all games have picks
    if (isSurvivorContest) {
      const numGames = (contest?.games_data || []).length
      const numPicks = Object.keys(survivorPicks).length
      return numGames > 0 && numPicks >= numGames
    }
    
    // For DFS: check if all roster slots are filled
    if (!contest?.rosterRules?.positions) return false
    
    const positions = contest.rosterRules.positions
    let complete = true
    
    Object.entries(positions).forEach(([position, posData]) => {
      // Handle both formats: simple number OR complex object {count, ...}
      const count = typeof posData === 'object' && posData !== null 
        ? (posData as any).count || 1 
        : posData as number;
      
      for (let i = 0; i < count; i++) {
        const slotKey = count > 1 ? `${position}${i + 1}` : position
        if (!roster[slotKey]) {
          complete = false
        }
      }
    })
    
    return complete
  }, [contest, roster, isSurvivorContest, survivorPicks])
  
  // Filter and sort available players (DFS only - survivor picks teams)
  const availablePlayers = useMemo(() => {
    // Survivor contests don't have a player pool
    if (isSurvivorContest) return []
    
    if (!contest?.playerPool) return []
    
    // First, deduplicate the player pool by ID
    const seenIds = new Set<string>()
    const rosterPlayerIds = new Set(Object.values(roster))
    
    let players = contest.playerPool.filter(player => {
      // Skip duplicates
      if (seenIds.has(player.id)) {
        console.log('⚠️ Skipping duplicate player:', player.name, player.id)
        return false
      }
      seenIds.add(player.id)
      
      // Exclude already selected players
      if (rosterPlayerIds.has(player.id)) {
        console.log('⚠️ Skipping rostered player:', player.name)
        return false
      }
      
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        if (!player.name.toLowerCase().includes(query) &&
            !player.team.toLowerCase().includes(query)) {
          return false
        }
      }
      
      // Position filter
      if (positionFilter !== 'ALL' && player.position !== positionFilter) {
        return false
      }
      
      return true
    })
    
    // Sort
    players.sort((a, b) => {
      switch (sortBy) {
        case 'salary':
          return (b.salary || 0) - (a.salary || 0)
        case 'projected':
          return (b.projectedPoints || 0) - (a.projectedPoints || 0)
        case 'name':
          return a.name.localeCompare(b.name)
        default:
          return 0
      }
    })
    
    console.log('📋 Available players:', players.length, '| Rostered:', rosterPlayerIds.size, '| Budget remaining:', salaryRemaining)
    
    return players
  }, [contest, roster, searchQuery, positionFilter, sortBy, salaryRemaining, isSurvivorContest])
  
  // Get unique positions from player pool
  const positions = useMemo(() => {
    if (!contest?.playerPool) return []
    const posSet = new Set(contest.playerPool.map(p => p.position))
    return ['ALL', ...Array.from(posSet)]
  }, [contest])
  
  // Add player to roster
  const addPlayer = useCallback((player: Player, slotKey: string) => {
    console.log('🏈 Adding player:', player.name, 'to slot:', slotKey)
    
    // Check salary cap
    if (contest?.rosterRules?.salaryCap && contest.rosterRules.salaryCap > 0) {
      const newTotal = salaryUsed + (player.salary || 0)
      if (newTotal > salaryCap) {
        toast.error(`Not enough salary cap! Need $${(player.salary || 0).toLocaleString()}, only $${salaryRemaining.toLocaleString()} remaining.`)
        return
      }
    }
    
    setRoster(prev => {
      const updated = {
        ...prev,
        [slotKey]: player.id
      }
      console.log('📋 Updated roster:', updated)
      return updated
    })
    setExpandedPosition(null)
    toast.success(`Added ${player.name} to ${slotKey}`)
  }, [contest?.rosterRules?.salaryCap, salaryUsed, salaryCap, salaryRemaining])
  
  // Remove player from roster
  const removePlayer = (slotKey: string) => {
    const player = findPlayerById(roster[slotKey])
    setRoster(prev => {
      const updated = { ...prev }
      delete updated[slotKey]
      return updated
    })
    if (player) {
      toast.success(`Removed ${player.name}`)
    }
  }
  
  // Submit roster
  const submitRoster = async () => {
    if (!user) {
      toast.error('Please connect your wallet to enter')
      return
    }
    
    if (!isRosterComplete) {
      toast.error('Please complete your roster')
      return
    }
    
    setSubmitting(true)
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/fantasy/contests/${id}/enter`,
        {
          userId: user.id,
          roster,
          entryName: entryName || undefined,
          entryId: currentEntryId || undefined,
          createNew: !currentEntryId && myEntries.length > 0 // Force new if no current entry but user has entries
        }
      )
      
      if (response.data.success) {
        toast.success(response.data.message || (existingEntry ? 'Roster updated!' : 'Entry submitted!'))
        
        // Refresh entries list
        await loadExistingEntry()
        // Refresh leaderboard so the new/updated entry shows immediately
        await loadLeaderboard()
        
        // If this was a new entry, set it as current
        if (response.data.entryId && !currentEntryId) {
          setCurrentEntryId(response.data.entryId)
        }
      }
    } catch (error: any) {
      console.error('Submit failed:', error)
      toast.error(error.response?.data?.error || 'Failed to submit entry')
    } finally {
      setSubmitting(false)
    }
  }
  
  // ============================================================================
  // RENDER
  // ============================================================================
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900/10 to-slate-900 flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    )
  }
  
  if (!contest) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900/10 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Contest Not Found</h2>
          <button
            onClick={() => navigate('/markets?category=fantasy')}
            className="text-emerald-400 hover:underline"
          >
            Back to Fantasy Contests
          </button>
        </div>
      </div>
    )
  }
  
  // ============================================================================
  // ROLLING LOCK LOGIC - Check if roster is editable based on game windows
  // ============================================================================
  const now = new Date()
  const firstLockTime = new Date(contest.roster_lock_time)
  const hasRollingLock = contest.rolling_lock === 1 && contest.game_windows && contest.game_windows.length > 0
  
  // Find current game phase and lock status
  const getLockStatus = (): { isLocked: boolean; currentGame: GameWindow | null; nextWindow: { type: 'lock' | 'unlock'; time: Date; game: GameWindow } | null; message: string } => {
    if (!hasRollingLock) {
      // Simple lock - just check the first lock time
      const locked = now >= firstLockTime
      return {
        isLocked: locked,
        currentGame: null,
        nextWindow: locked ? null : { type: 'lock', time: firstLockTime, game: contest.game_windows?.[0] || null as any },
        message: locked ? 'Rosters are locked' : `Locks ${firstLockTime.toLocaleString()}`
      }
    }
    
    // Rolling lock - check each game window
    const gameWindows = contest.game_windows!
    
    for (let i = 0; i < gameWindows.length; i++) {
      const window = gameWindows[i]
      const lockTime = new Date(window.lockTime)
      const unlockTime = window.unlockTime ? new Date(window.unlockTime) : null
      const gameEnd = new Date(window.gameEndTime)
      
      // Before this game's lock time - editable
      if (now < lockTime) {
        return {
          isLocked: false,
          currentGame: window,
          nextWindow: { type: 'lock', time: lockTime, game: window },
          message: `Editable until Game ${window.gameNumber} locks`
        }
      }
      
      // During game (locked) - check if we're between lock and unlock
      if (now >= lockTime && (!unlockTime || now < unlockTime)) {
        // Check if game has ended (might unlock soon)
        if (unlockTime && now >= gameEnd) {
          return {
            isLocked: true,
            currentGame: window,
            nextWindow: { type: 'unlock', time: unlockTime, game: window },
            message: `Game ${window.gameNumber} ended - unlocks soon for next game`
          }
        }
        return {
          isLocked: true,
          currentGame: window,
          nextWindow: unlockTime ? { type: 'unlock', time: unlockTime, game: window } : null,
          message: `Locked during Game ${window.gameNumber}`
        }
      }
      
      // After unlock but before next game's lock - editable window!
      if (unlockTime && now >= unlockTime) {
        const nextGame = gameWindows[i + 1]
        if (nextGame) {
          const nextLock = new Date(nextGame.lockTime)
          if (now < nextLock) {
            return {
              isLocked: false,
              currentGame: nextGame,
              nextWindow: { type: 'lock', time: nextLock, game: nextGame },
              message: `✏️ Edit window open! Swap players before Game ${nextGame.gameNumber}`
            }
          }
        }
      }
    }
    
    // Contest is over
    return {
      isLocked: true,
      currentGame: null,
      nextWindow: null,
      message: 'Contest completed'
    }
  }
  
  const lockStatus = getLockStatus()
  const isLocked = lockStatus.isLocked
  const lockTime = firstLockTime
  
  return (
    <div className="min-h-screen py-8 bg-gradient-to-br from-slate-900 via-emerald-900/10 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header - Similar to Market Detail Page */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/markets?category=fantasy')}
            className="btn-secondary flex items-center space-x-2 mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Fantasy</span>
          </button>

          <div className="card p-6 border-l-4 border-emerald-500 bg-slate-800/50">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h1 className="text-3xl font-black text-white mb-2 flex items-center gap-3">
                  <span className="text-3xl">🏈</span>
                  {contest.title}
                </h1>
                <div className="flex items-center flex-wrap gap-4 text-sm text-gray-400">
                  <span className="px-2 py-1 rounded text-xs font-bold bg-emerald-500/20 text-emerald-400 uppercase tracking-wider">
                    Fantasy Football
                  </span>
                  <span className="flex items-center gap-1">
                    <DollarSign className="w-4 h-4 text-emerald-400" />
                    {contest.entry_fee > 0 ? `$${contest.entry_fee} Entry` : 'Free Entry'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Trophy className="w-4 h-4 text-yellow-400" />
                    ${contest.prize_pool || 0} Prize Pool
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4 text-blue-400" />
                    {contest.current_entries || contest.entryCount || 0} / {contest.max_entries === 0 ? '∞' : contest.max_entries} Entries
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className={`px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-wider ${
                  isLocked ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'
                }`}>
                  {isLocked ? '🔒 Locked' : '🟢 Open'}
                </div>
                {user && (
                  <button
                    onClick={async () => {
                      if (window.confirm(`Are you sure you want to delete "${contest.title}"?\n\nThis will delete all entries and cannot be undone.`)) {
                        try {
                          await axios.delete(`${API_BASE_URL}/api/fantasy/contests/${id}?userId=${user.id}`)
                          toast.success('Contest deleted!')
                          navigate('/markets?category=fantasy')
                        } catch (error: any) {
                          toast.error(error.response?.data?.error || 'Failed to delete contest')
                        }
                      }
                    }}
                    className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                    title="Delete Contest"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>

            {contest.description && (
              <p className="text-gray-300 mb-4">{contest.description}</p>
            )}

            <div className="flex items-center justify-between text-sm text-gray-200">
              <span className="flex items-center gap-2">
                {isLocked ? (
                  <Lock className="w-4 h-4 text-red-400" />
                ) : (
                  <Clock className="w-4 h-4 text-emerald-400" />
                )}
                {isLocked ? 'Contest Locked' : `Locks: ${lockTime.toLocaleString('en-US', { 
                  weekday: 'short', 
                  month: 'short', 
                  day: 'numeric', 
                  hour: 'numeric', 
                  minute: '2-digit' 
                })}`}
              </span>
              <span>🕐 Created: {new Date(contest.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      
        {/* Lock Time Banner - shows countdown and rolling lock status */}
        <div className={`card mb-6 p-4 ${
          isLocked 
            ? 'bg-gradient-to-r from-red-900/20 to-orange-900/20 border-red-500/30'
            : 'bg-gradient-to-r from-emerald-900/20 to-green-900/20 border-emerald-500/30'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isLocked ? (
                <Lock className="w-5 h-5 text-red-400" />
              ) : (
                <Clock className="w-5 h-5 text-emerald-400" />
              )}
              <span className={`font-medium ${isLocked ? 'text-red-300' : 'text-emerald-300'}`}>
                {lockStatus.message}
              </span>
            </div>
            
            {/* Countdown to next event */}
            {lockStatus.nextWindow && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-500">
                  {lockStatus.nextWindow.type === 'lock' ? 'Locks in:' : 'Unlocks in:'}
                </span>
                <span className={lockStatus.nextWindow.type === 'lock' ? 'text-yellow-400' : 'text-emerald-400'}>
                  {(() => {
                    const diff = lockStatus.nextWindow.time.getTime() - Date.now();
                    if (diff <= 0) return 'Now';
                    const hours = Math.floor(diff / (1000 * 60 * 60));
                    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                    if (hours > 24) {
                      const days = Math.floor(hours / 24);
                      return `${days}d ${hours % 24}h`;
                    }
                    return `${hours}h ${minutes}m`;
                  })()}
                </span>
              </div>
            )}
          </div>
          
          {/* Rolling Lock Game Windows */}
          {hasRollingLock && contest.game_windows && (
            <div className="mt-3 pt-3 border-t border-slate-700/50">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-gray-500 uppercase tracking-wide">Game Schedule (Points Carry Over)</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {contest.game_windows.map((window, i) => {
                  const windowLockTime = new Date(window.lockTime)
                  const windowUnlockTime = window.unlockTime ? new Date(window.unlockTime) : null
                  const gameEnd = new Date(window.gameEndTime)
                  const isCurrentGame = lockStatus.currentGame?.gameNumber === window.gameNumber
                  const isPast = windowUnlockTime ? now > windowUnlockTime : now > gameEnd
                  const isLive = now >= windowLockTime && !isPast
                  
                  return (
                    <div
                      key={i}
                      className={`px-3 py-2 rounded-lg text-xs border ${
                        isCurrentGame && isLocked
                          ? 'bg-red-500/20 border-red-500/50 text-red-300'
                          : isCurrentGame
                          ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300'
                          : isPast
                          ? 'bg-slate-800/50 border-slate-700/50 text-gray-500'
                          : 'bg-slate-800/50 border-slate-600/50 text-gray-400'
                      }`}
                    >
                      <div className="font-medium">
                        {isLive && '🔴 '}{window.description}
                      </div>
                      <div className="text-[10px] mt-1 opacity-75">
                        {new Date(window.gameStartTime).toLocaleString('en-US', { 
                          weekday: 'short', 
                          month: 'short', 
                          day: 'numeric',
                          hour: 'numeric', 
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      
        {/* TAB NAVIGATION */}
        <div className="flex gap-2 border-b border-slate-700 mb-6">
          <button
            onClick={() => setActiveTab('roster')}
            className={`px-4 py-3 font-medium text-sm transition-all border-b-2 -mb-px ${
              activeTab === 'roster'
                ? 'text-emerald-400 border-emerald-400'
                : 'text-gray-400 border-transparent hover:text-white'
            }`}
          >
            <UserPlus className="w-4 h-4 inline mr-2" />
            {existingEntry ? 'Your Roster' : 'Build Roster'}
          </button>
          <button
            onClick={() => { setActiveTab('leaderboard'); loadLeaderboard(); }}
            className={`px-4 py-3 font-medium text-sm transition-all border-b-2 -mb-px ${
              activeTab === 'leaderboard'
                ? 'text-amber-400 border-amber-400'
                : 'text-gray-400 border-transparent hover:text-white'
            }`}
          >
            <Trophy className="w-4 h-4 inline mr-2" />
            Leaderboard
            <span className="ml-2 text-xs bg-slate-700 px-2 py-0.5 rounded-full">
              {leaderboard.length}
            </span>
          </button>
          {existingEntry && (
            <button
              onClick={() => setActiveTab('scores')}
              className={`px-4 py-3 font-medium text-sm transition-all border-b-2 -mb-px ${
                activeTab === 'scores'
                  ? 'text-emerald-400 border-emerald-400'
                  : 'text-gray-400 border-transparent hover:text-white'
              }`}
            >
              <Zap className="w-4 h-4 inline mr-2" />
              Your Scores
            </button>
          )}
          <button
            onClick={() => setActiveTab('rules')}
            className={`px-4 py-3 font-medium text-sm transition-all border-b-2 -mb-px ${
              activeTab === 'rules'
                ? 'text-blue-400 border-blue-400'
                : 'text-gray-400 border-transparent hover:text-white'
            }`}
          >
            <Filter className="w-4 h-4 inline mr-2" />
            Scoring & Rules
          </button>
        </div>
      </div>
      
      {/* LEADERBOARD TAB */}
      {activeTab === 'leaderboard' && (
        <div className="max-w-7xl mx-auto px-4 pb-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Leaderboard - Takes 2/3 of space on large screens */}
            <div className="lg:col-span-2 bg-slate-800/50 rounded-xl border border-amber-500/20">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <h2 className="font-bold text-white flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-400" />
                Leaderboard
                <span className="text-sm font-normal text-gray-400">
                  ({leaderboard.length} {leaderboard.length === 1 ? 'entry' : 'entries'})
                </span>
              </h2>
              <button 
                onClick={loadLeaderboard}
                className="text-xs text-gray-400 hover:text-white transition-colors flex items-center gap-1"
              >
                {loadingLeaderboard ? <Loader className="w-3 h-3 animate-spin" /> : null}
                Refresh
              </button>
            </div>
            
            <div className="p-4">
              {loadingLeaderboard ? (
                <div className="text-center py-8">
                  <Loader className="w-8 h-8 animate-spin text-amber-500 mx-auto" />
                  <p className="text-gray-400 mt-2">Loading standings...</p>
                </div>
              ) : leaderboard.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400 text-lg">No entries yet</p>
                  <p className="text-sm text-gray-500">Be the first to enter this contest!</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Find current user's rank */}
                  {(() => {
                    const userEntry = leaderboard.find(e => e.user_id === user?.id)
                    const userRank = userEntry ? leaderboard.indexOf(userEntry) + 1 : null
                    
                    return userEntry ? (
                      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                              userRank === 1 ? 'bg-amber-500 text-black' :
                              userRank === 2 ? 'bg-gray-300 text-black' :
                              userRank === 3 ? 'bg-amber-700 text-white' :
                              'bg-emerald-600 text-white'
                            }`}>
                              {userRank}
                            </div>
                            <div>
                              <div className="font-semibold text-white">Your Position</div>
                              <div className="text-sm text-gray-400">
                                {userRank === 1 ? '🏆 You\'re in first place!' :
                                 userRank && userRank <= 3 ? '🎉 You\'re on the podium!' :
                                 `${leaderboard.length - (userRank || 0)} entries ahead of you`}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold text-emerald-400">
                              {(userEntry.total_points || 0).toFixed(1)}
                            </div>
                            <div className="text-xs text-gray-500">points</div>
                          </div>
                        </div>
                      </div>
                    ) : null
                  })()}
                  
                  {/* Leaderboard list - Each ENTRY is a row (not user) */}
                  {/* Show survivors first, then eliminated */}
                  <div className="max-h-[500px] overflow-y-auto space-y-2">
                    {/* Survivors / Active entries */}
                    {leaderboard.filter(e => !e.eliminated).map((entry, index) => {
                      const isCurrentUser = user?.id === entry.user_id
                      const rank = entry.rank || index + 1
                      const displayName = entry.entry_name || `Entry #${entry.entry_number || 1}`
                      
                      return (
                        <div 
                          key={entry.id}
                          className={`flex items-center justify-between p-3 rounded-lg transition-all ${
                            isCurrentUser 
                              ? 'bg-emerald-500/20 border border-emerald-500/30' 
                              : 'bg-slate-900/30 hover:bg-slate-800/50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                              rank === 1 ? 'bg-amber-500 text-black' :
                              rank === 2 ? 'bg-gray-300 text-black' :
                              rank === 3 ? 'bg-amber-700 text-white' :
                              'bg-slate-700 text-gray-300'
                            }`}>
                              {rank}
                            </div>
                            
                            <div>
                              <div className="font-medium text-white flex items-center gap-2">
                                {entry.username || `User ${entry.user_id}`}
                                {isCurrentUser && (
                                  <span className="text-xs bg-emerald-500/30 text-emerald-400 px-2 py-0.5 rounded">
                                    You
                                  </span>
                                )}
                                {contest?.contest_category === 'survivor' && (
                                  <span className="text-xs bg-green-500/30 text-green-400 px-2 py-0.5 rounded">
                                    ALIVE
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-400 flex items-center gap-2">
                                <span className="bg-slate-700 px-1.5 py-0.5 rounded text-blue-300">
                                  {displayName}
                                </span>
                                {entry.correct_picks !== undefined && (
                                  <>
                                    <span className="text-gray-500">•</span>
                                    <span className="text-green-400">{entry.correct_picks} correct</span>
                                  </>
                                )}
                              </div>
                              {/* Show picks for survivor contests */}
                              {contest?.contest_category === 'survivor' && entry.roster && Object.keys(entry.roster).length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {Object.entries(entry.roster).map(([gameId, team]: [string, any]) => (
                                    <span 
                                      key={gameId}
                                      className={`text-xs px-2 py-0.5 rounded ${
                                        entry.captain_pick === gameId 
                                          ? 'bg-amber-500/30 text-amber-300 border border-amber-500/30' 
                                          : 'bg-slate-700/50 text-gray-300'
                                      }`}
                                      title={`${gameId}: ${team}${entry.captain_pick === gameId ? ' (Captain 2x)' : ''}`}
                                    >
                                      {entry.captain_pick === gameId ? '👑 ' : ''}{String(team).split(' ').pop()}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <div className={`text-lg font-bold ${
                              entry.total_points > 0 ? 'text-emerald-400' : 'text-gray-400'
                            }`}>
                              {(entry.total_points || 0).toFixed(1)} pts
                            </div>
                            {/* Per-game scores for multi-game contests */}
                            {entry.points_by_game && Object.keys(entry.points_by_game).length > 0 && (
                              <div className="flex gap-1 mt-1 justify-end">
                                {Object.entries(entry.points_by_game).map(([game, pts]: [string, any]) => (
                                  <span 
                                    key={game} 
                                    className="text-xs bg-slate-700/50 px-1.5 py-0.5 rounded text-gray-400"
                                    title={`Game ${game}`}
                                  >
                                    G{game}: {Number(pts).toFixed(1)}
                                  </span>
                                ))}
                              </div>
                            )}
                            {entry.rank && entry.rank !== index + 1 && (
                              <div className="text-xs text-gray-500">
                                Final Rank: #{entry.rank}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                    
                    {/* Eliminated entries - grayed out */}
                    {leaderboard.filter(e => e.eliminated).length > 0 && (
                      <>
                        <div className="border-t border-slate-700 my-4 pt-4">
                          <div className="text-sm text-gray-500 mb-2 flex items-center gap-2">
                            <span>☠️</span>
                            <span>ELIMINATED ({leaderboard.filter(e => e.eliminated).length})</span>
                          </div>
                        </div>
                        {leaderboard.filter(e => e.eliminated).map((entry) => {
                          const isCurrentUser = user?.id === entry.user_id
                          const displayName = entry.entry_name || `Entry #${entry.entry_number || 1}`
                          
                          return (
                            <div 
                              key={entry.id}
                              className={`flex items-center justify-between p-3 rounded-lg transition-all opacity-50 ${
                                isCurrentUser 
                                  ? 'bg-red-500/10 border border-red-500/30' 
                                  : 'bg-slate-900/20'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full flex items-center justify-center text-lg bg-slate-800">
                                  ☠️
                                </div>
                                
                                <div>
                                  <div className="font-medium text-gray-400 flex items-center gap-2 line-through">
                                    {entry.username || `User ${entry.user_id}`}
                                    {isCurrentUser && (
                                      <span className="text-xs bg-red-500/30 text-red-400 px-2 py-0.5 rounded no-underline">
                                        You
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-500 flex items-center gap-2">
                                    <span className="bg-slate-800 px-1.5 py-0.5 rounded text-gray-500">
                                      {displayName}
                                    </span>
                                    <span className="text-red-400">
                                      Out on Game {entry.eliminated_at_game || '?'}
                                    </span>
                                  </div>
                                  {entry.eliminated_reason && (
                                    <div className="text-xs text-gray-600 mt-1 italic">
                                      {entry.eliminated_reason}
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              <div className="text-right">
                                <div className="text-lg font-bold text-gray-600">
                                  {entry.correct_picks || 0}/{entry.total_picks || entry.eliminated_at_game || '?'}
                                </div>
                                <div className="text-xs text-red-400">
                                  ELIMINATED
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Chat Panel - Takes 1/3 of space on large screens */}
          <div className="lg:col-span-1">
            <FantasyChat 
              contestId={contest?.id || 0} 
              contestStatus={contest?.status || 'open'} 
            />
          </div>
          </div>
        </div>
      )}
      
      {/* YOUR SCORES TAB - Score Breakdown */}
      {activeTab === 'scores' && existingEntry && (
        <div className="max-w-4xl mx-auto px-4 pb-6">
          <div className="bg-slate-800/50 rounded-xl border border-emerald-500/20">
            <div className="p-4 border-b border-slate-700">
              <h2 className="font-bold text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-emerald-400" />
                Your Score Breakdown
                <span className="text-sm font-normal text-gray-400">
                  Total: {(existingEntry.total_points || 0).toFixed(1)} pts
                </span>
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                See exactly how each player earned points using this contest's scoring rules
              </p>
            </div>
            
            <div className="p-4 space-y-4">
              {existingEntry.playerScores && existingEntry.playerScores.length > 0 ? (
                existingEntry.playerScores.map((player: any, idx: number) => (
                  <div key={idx} className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                    {/* Player Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 text-xs font-bold rounded ${
                          player.position === 'QB' ? 'bg-red-500/20 text-red-300' :
                          player.position === 'RB' ? 'bg-blue-500/20 text-blue-300' :
                          player.position === 'WR' ? 'bg-green-500/20 text-green-300' :
                          player.position === 'TE' ? 'bg-purple-500/20 text-purple-300' :
                          player.position === 'K' ? 'bg-yellow-500/20 text-yellow-300' :
                          'bg-gray-500/20 text-gray-300'
                        }`}>
                          {player.position}
                        </span>
                        <div>
                          <span className="font-semibold text-white">{player.playerName}</span>
                          <span className="text-gray-500 text-sm ml-2">{player.team}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold text-emerald-400">
                          {(player.fantasyPoints || 0).toFixed(1)}
                        </div>
                        <div className="text-xs text-gray-500">points</div>
                      </div>
                    </div>
                    
                    {/* Point Breakdown */}
                    {player.pointBreakdown && Object.keys(player.pointBreakdown).length > 0 ? (
                      <div className="space-y-1 border-t border-slate-700 pt-3">
                        {Object.entries(player.pointBreakdown).map(([stat, points]: [string, any]) => (
                          <div key={stat} className="flex items-center justify-between text-sm">
                            <span className="text-gray-400">{stat}</span>
                            <span className={`font-mono ${points >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {points >= 0 ? '+' : ''}{points.toFixed(2)}
                            </span>
                          </div>
                        ))}
                        <div className="flex items-center justify-between text-sm font-semibold border-t border-slate-600 pt-2 mt-2">
                          <span className="text-white">Total</span>
                          <span className="text-emerald-400">{(player.fantasyPoints || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500 italic border-t border-slate-700 pt-3">
                        No stats recorded yet (game may not have started)
                      </div>
                    )}
                    
                    {/* Raw Stats (collapsible) */}
                    {player.stats && Object.keys(player.stats).filter(k => !['playerId', 'playerName', 'team', 'position'].includes(k) && player.stats[k]).length > 0 && (
                      <details className="mt-3">
                        <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-400">
                          View raw stats
                        </summary>
                        <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                          {Object.entries(player.stats)
                            .filter(([k, v]) => !['playerId', 'playerName', 'team', 'position'].includes(k) && v)
                            .map(([stat, value]: [string, any]) => (
                              <div key={stat} className="flex justify-between bg-slate-800 px-2 py-1 rounded">
                                <span className="text-gray-500">{stat}</span>
                                <span className="text-gray-300">{value}</span>
                              </div>
                            ))}
                        </div>
                      </details>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <Zap className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">No scores yet</p>
                  <p className="text-sm text-gray-500">
                    Scores will appear here once games start and stats are recorded
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* RULES TAB - Full Contest Plan */}
      {activeTab === 'rules' && (
        <div className="max-w-7xl mx-auto px-4 pb-6 space-y-6">
          {/* Games Section */}
          {(contest?.games_data || contest?.teams) && (
            <div className="bg-slate-800/50 rounded-xl border border-amber-500/20 p-6">
              <h2 className="font-bold text-white text-lg mb-4 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-400" />
                Games Included
              </h2>
              
              {contest.games_data ? (
                <div className="space-y-3">
                  {contest.games_data.map((game: any, i: number) => (
                    <div key={i} className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-900 to-slate-800 rounded-lg border border-slate-700">
                      <div className="flex items-center gap-4">
                        <span className="text-2xl font-bold text-white">{game.awayTeam}</span>
                        <span className="text-gray-500">@</span>
                        <span className="text-2xl font-bold text-white">{game.homeTeam}</span>
                      </div>
                      <div className="text-right">
                        <p className="text-emerald-400 font-medium">
                          {new Date(game.gameDate).toLocaleDateString('en-US', { 
                            weekday: 'short', month: 'short', day: 'numeric' 
                          })}
                        </p>
                        {game.gameTime && (
                          <p className="text-gray-500 text-sm">{game.gameTime}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : contest.teams && (
                <div className="flex flex-wrap gap-3">
                  {contest.teams.map((team: string, i: number) => (
                    <span key={i} className="px-4 py-2 bg-slate-900 rounded-lg text-white font-medium">
                      {team}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Timing Section */}
          <div className="bg-slate-800/50 rounded-xl border border-purple-500/20 p-6">
            <h2 className="font-bold text-white text-lg mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-purple-400" />
              Timing & Locks
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {contest?.roster_lock_time && (
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase mb-1">Roster Lock</p>
                  <p className="text-lg font-bold text-purple-400">
                    {new Date(contest.roster_lock_time).toLocaleString('en-US', {
                      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                    })}
                  </p>
                </div>
              )}
              
              {contest?.first_game_time && (
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase mb-1">First Game</p>
                  <p className="text-lg font-bold text-emerald-400">
                    {new Date(contest.first_game_time).toLocaleString('en-US', {
                      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                    })}
                  </p>
                </div>
              )}
              
              {contest?.last_game_time && (
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase mb-1">Last Game</p>
                  <p className="text-lg font-bold text-amber-400">
                    {new Date(contest.last_game_time).toLocaleString('en-US', {
                      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                    })}
                  </p>
                </div>
              )}
              
              {contest?.resolution_time && (
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase mb-1">Results</p>
                  <p className="text-lg font-bold text-blue-400">
                    {new Date(contest.resolution_time).toLocaleString('en-US', {
                      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                    })}
                  </p>
                </div>
              )}
            </div>
            
            {/* Rolling Lock Info */}
            {contest?.rolling_lock && (
              <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex items-center gap-2 text-amber-400 font-semibold mb-2">
                  <Lock className="w-4 h-4" />
                  Rolling Lock Enabled
                </div>
                <p className="text-gray-400 text-sm">
                  You can edit your roster between games! Rosters lock {contest.rolling_lock} minutes before each game starts.
                </p>
              </div>
            )}
            
            {/* Game Windows */}
            {contest?.game_windows && contest.game_windows.length > 0 && (
              <div className="mt-4">
                <h3 className="font-semibold text-white mb-3">Game Windows</h3>
                <div className="space-y-2">
                  {contest.game_windows.map((window: GameWindow, i: number) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                      <span className="text-gray-300">Game {window.gameNumber}</span>
                      <span className="text-sm text-gray-500">{window.description}</span>
                      <span className="text-purple-400 text-sm">
                        Locks: {new Date(window.lockTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Scoring Section - Shows TEAM scoring for survivor, PLAYER scoring for DFS */}
          <div className="bg-slate-800/50 rounded-xl border border-blue-500/20 p-6">
            <h2 className="font-bold text-white text-lg mb-4 flex items-center gap-2">
              <Filter className="w-5 h-5 text-blue-400" />
              {contest?.contest_category === 'survivor' ? 'Team Scoring System' : 'Scoring System'}
              {contest?.scoringSystem?.name && (
                <span className="text-sm font-normal text-blue-400 ml-2">({contest.scoringSystem.name})</span>
              )}
            </h2>
            
            {/* 🏆 TEAM SCORING - For Survivor/Pick'em contests */}
            {contest?.contest_category === 'survivor' ? (
              <div className="space-y-4">
                {/* Description */}
                {contest?.scoringSystem?.description && (
                  <p className="text-gray-300 mb-4">{contest.scoringSystem.description}</p>
                )}
                
                {/* Team Scoring Rules */}
                {contest?.scoringSystem?.rules && contest.scoringSystem.rules.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-amber-400 font-semibold mb-2">📊 Point Values</h3>
                    <div className="grid md:grid-cols-2 gap-3">
                      {contest.scoringSystem.rules.map((rule: any, i: number) => (
                        <div key={i} className="flex justify-between items-center p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                          <span className="text-gray-300">{rule.stat || rule.description}</span>
                          <span className="font-bold text-amber-400">
                            {rule.multiplier || rule.points || 1} pt{(rule.multiplier || rule.points || 1) !== 1 ? 's' : ''}/yard
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Bonus Thresholds */}
                {contest?.scoringSystem?.bonuses && contest.scoringSystem.bonuses.length > 0 && (
                  <div className="mt-4 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <h3 className="text-green-400 font-semibold mb-2">🎯 Bonus Thresholds</h3>
                    <div className="space-y-2">
                      {contest.scoringSystem.bonuses.map((bonus: any, i: number) => (
                        <div key={i} className="flex justify-between items-center">
                          <span className="text-gray-300">Team gets {bonus.threshold}+ total yards</span>
                          <span className="font-bold text-green-400">+{bonus.points} bonus pts</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Formula */}
                {contest?.scoringSystem?.formula && (
                  <div className="mt-4 p-3 bg-slate-900/50 rounded-lg">
                    <span className="text-gray-400 text-sm">Formula: </span>
                    <code className="text-blue-400 font-mono">{contest.scoringSystem.formula}</code>
                  </div>
                )}
                
                {/* Example Calculation */}
                {contest?.scoringSystem?.example && (
                  <div className="mt-4 p-4 bg-slate-900/70 rounded-lg border border-slate-700">
                    <h3 className="text-gray-400 text-sm mb-2">📝 Example Calculation</h3>
                    <p className="text-white">{contest.scoringSystem.example}</p>
                  </div>
                )}
                
                {/* If no detailed scoring, show a default explanation */}
                {!contest?.scoringSystem?.rules && !contest?.scoringSystem?.description && (
                  <div className="text-gray-400">
                    <p>Pick the team with more total yards in each game.</p>
                    <p className="mt-2">Your score = total yards accumulated by your picked teams across all games.</p>
                  </div>
                )}
              </div>
            ) : contest?.scoringSystem?.formulas && contest.scoringSystem.formulas.length > 0 ? (
              /* Formula-based PLAYER scoring (new format) */
              <div className="space-y-4">
                {/* Scoring System Name */}
                {contest.scoringSystem.name && (
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg font-bold text-emerald-400">{contest.scoringSystem.name}</span>
                  </div>
                )}
                {contest.scoringSystem.description && (
                  <p className="text-gray-300 text-sm mb-4 p-3 bg-slate-900/50 rounded-lg border border-slate-700">
                    {contest.scoringSystem.description}
                  </p>
                )}
                <h3 className="text-amber-400 font-semibold">📊 Point Formulas</h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {contest.scoringSystem.formulas.map((formula: any, i: number) => (
                    <div key={i} className="flex justify-between items-center p-3 bg-slate-900/50 rounded-lg">
                      <div>
                        <span className="text-gray-300">{formula.name}</span>
                        {formula.appliesTo && !formula.appliesTo.includes('ALL') && (
                          <span className="text-xs text-gray-500 ml-2">({formula.appliesTo.join(', ')})</span>
                        )}
                      </div>
                      <span className="text-blue-400 font-mono text-sm">{formula.description || formula.formula}</span>
                    </div>
                  ))}
                </div>
                {/* Example calculation if provided */}
                {contest.scoringSystem.example && (
                  <div className="mt-4 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                    <h3 className="text-emerald-400 font-semibold mb-2">📝 Example Calculation</h3>
                    <p className="text-white">{contest.scoringSystem.example}</p>
                  </div>
                )}
              </div>
            ) : contest?.scoringSystem?.rules && contest.scoringSystem.rules.length > 0 ? (
              /* Legacy rules format */
              <div className="grid md:grid-cols-2 gap-3">
                {contest.scoringSystem.rules.map((rule: any, i: number) => (
                  <div key={i} className="flex justify-between items-center p-3 bg-slate-900/50 rounded-lg">
                    <span className="text-gray-300">{rule.description || rule.stat}</span>
                    <span className={`font-bold ${rule.points >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {rule.points >= 0 ? '+' : ''}{rule.points} pts
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              /* Show multiplier values directly or default standard scoring */
              <div className="space-y-4">
                {/* Show name/description if available even without formulas */}
                {contest?.scoringSystem?.name && (
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg font-bold text-emerald-400">{contest.scoringSystem.name}</span>
                  </div>
                )}
                {contest?.scoringSystem?.description && (
                  <p className="text-gray-300 text-sm mb-4 p-3 bg-slate-900/50 rounded-lg border border-slate-700">
                    {contest.scoringSystem.description}
                  </p>
                )}
                
                <h3 className="text-amber-400 font-semibold">📊 Point Values</h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {[
                    { stat: 'Passing Yards', value: contest?.scoringSystem?.passingYards ?? 0.04, per: 'per yard' },
                    { stat: 'Passing TD', value: contest?.scoringSystem?.passingTD ?? 4, per: 'per TD' },
                    { stat: 'Interception', value: contest?.scoringSystem?.interception ?? -1, per: 'per INT' },
                    { stat: 'Rushing Yards', value: contest?.scoringSystem?.rushingYards ?? 0.1, per: 'per yard' },
                    { stat: 'Rushing TD', value: contest?.scoringSystem?.rushingTD ?? 6, per: 'per TD' },
                    { stat: 'Receiving Yards', value: contest?.scoringSystem?.receivingYards ?? 0.1, per: 'per yard' },
                    { stat: 'Receiving TD', value: contest?.scoringSystem?.receivingTD ?? 6, per: 'per TD' },
                    { stat: 'Reception (PPR)', value: contest?.scoringSystem?.reception ?? 1, per: 'per catch' },
                    { stat: 'Fumble Lost', value: contest?.scoringSystem?.fumbleLost ?? -2, per: 'per fumble' },
                  ].map((item, i) => (
                    <div key={i} className="flex justify-between items-center p-3 bg-slate-900/50 rounded-lg">
                      <span className="text-gray-300">{item.stat}</span>
                      <span className={`font-bold ${item.value >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {item.value >= 0 ? '+' : ''}{item.value} {item.per}
                      </span>
                    </div>
                  ))}
                </div>
                
                {/* Example if provided */}
                {contest?.scoringSystem?.example && (
                  <div className="mt-4 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                    <h3 className="text-emerald-400 font-semibold mb-2">📝 Example Calculation</h3>
                    <p className="text-white">{contest.scoringSystem.example}</p>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Roster Rules Section */}
          <div className="bg-slate-800/50 rounded-xl border border-emerald-500/20 p-6">
            <h2 className="font-bold text-white text-lg mb-4 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-400" />
              Roster Rules
            </h2>
            
            <div className="grid md:grid-cols-3 gap-4">
              {contest?.rosterRules?.positions && Object.entries(contest.rosterRules.positions).map(([pos, posData]) => {
                // Handle both formats: simple number OR complex object {count, multiplier, description}
                const count = typeof posData === 'object' && posData !== null 
                  ? (posData as any).count || 1 
                  : posData as number;
                const multiplier = typeof posData === 'object' && posData !== null 
                  ? (posData as any).multiplier 
                  : null;
                const desc = typeof posData === 'object' && posData !== null 
                  ? (posData as any).description 
                  : null;
                
                return (
                  <div key={pos} className="flex justify-between items-center p-3 bg-slate-900/50 rounded-lg">
                    <span className="text-gray-300">
                      {pos}{desc ? ` (${desc})` : ''}
                    </span>
                    <span className="font-bold text-white">
                      {count}x{multiplier && multiplier !== 1 ? ` @ ${multiplier}×` : ''}
                    </span>
                  </div>
                );
              })}
              {contest?.rosterRules?.salaryCap && contest.rosterRules.salaryCap > 0 && (
                <div className="flex justify-between items-center p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                  <span className="text-gray-300">Salary Cap</span>
                  <span className="font-bold text-emerald-400">${contest.rosterRules.salaryCap.toLocaleString()}</span>
                </div>
              )}
              {contest?.rosterRules?.maxPlayersPerTeam && (
                <div className="flex justify-between items-center p-3 bg-slate-900/50 rounded-lg">
                  <span className="text-gray-300">Max per Team</span>
                  <span className="font-bold text-white">{contest.rosterRules.maxPlayersPerTeam}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Swap Rules Section - Only for rolling lock contests */}
          {contest?.rolling_lock === 1 && contest?.swap_rules && (
            <div className="bg-slate-800/50 rounded-xl border border-purple-500/20 p-6">
              <h2 className="font-bold text-white text-lg mb-4 flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-purple-400" />
                Roster Swap Rules
                <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">Rolling Lock</span>
              </h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                {/* Swaps per window */}
                <div className="flex justify-between items-center p-3 bg-slate-900/50 rounded-lg">
                  <span className="text-gray-300">Swaps per Window</span>
                  <span className="font-bold text-white">
                    {contest.swap_rules.max_swaps_per_window === null ? '♾️ Unlimited' : `${contest.swap_rules.max_swaps_per_window} max`}
                  </span>
                </div>
                
                {/* Total swaps */}
                <div className="flex justify-between items-center p-3 bg-slate-900/50 rounded-lg">
                  <span className="text-gray-300">Total Swaps</span>
                  <span className="font-bold text-white">
                    {contest.swap_rules.max_swaps_total === null ? '♾️ Unlimited' : `${contest.swap_rules.max_swaps_total} max`}
                  </span>
                </div>
                
                {/* Swap type */}
                <div className="flex justify-between items-center p-3 bg-slate-900/50 rounded-lg">
                  <span className="text-gray-300">Swap Restrictions</span>
                  <span className="font-bold text-white">
                    {contest.swap_rules.swap_type === 'any' && '✅ Swap anyone'}
                    {contest.swap_rules.swap_type === 'worst_only' && '⬇️ Worst player only'}
                    {contest.swap_rules.swap_type === 'locked_captain' && '👑 Captain locked'}
                  </span>
                </div>
                
                {/* Swap cost */}
                {contest.swap_rules.swap_cost_points !== 0 && (
                  <div className="flex justify-between items-center p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <span className="text-gray-300">Swap Cost</span>
                    <span className="font-bold text-red-400">
                      {contest.swap_rules.swap_cost_points} points per swap
                    </span>
                  </div>
                )}
                
                {/* No repeats */}
                {contest.swap_rules.no_repeat_swaps && (
                  <div className="flex justify-between items-center p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg col-span-2">
                    <span className="text-gray-300">🚫 No Repeat Players</span>
                    <span className="font-bold text-amber-400">
                      Once dropped, a player cannot be re-added
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Prize Structure */}
          {contest?.prizeStructure && (
            <div className="bg-slate-800/50 rounded-xl border border-yellow-500/20 p-6">
              <h2 className="font-bold text-white text-lg mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-yellow-400" />
                Prize Structure
              </h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                {contest.entry_fee !== undefined && (
                  <div className="flex justify-between items-center p-3 bg-slate-900/50 rounded-lg">
                    <span className="text-gray-300">Entry Fee</span>
                    <span className="font-bold text-white">
                      {contest.entry_fee === 0 ? 'FREE' : `$${contest.entry_fee}`}
                    </span>
                  </div>
                )}
                {contest.prize_pool !== undefined && contest.prize_pool > 0 && (
                  <div className="flex justify-between items-center p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <span className="text-gray-300">Prize Pool</span>
                    <span className="font-bold text-yellow-400">${contest.prize_pool.toLocaleString()}</span>
                  </div>
                )}
                {contest.max_entries && (
                  <div className="flex justify-between items-center p-3 bg-slate-900/50 rounded-lg">
                    <span className="text-gray-300">Max Entries</span>
                    <span className="font-bold text-white">{contest.max_entries}</span>
                  </div>
                )}
                {contest.current_entries !== undefined && (
                  <div className="flex justify-between items-center p-3 bg-slate-900/50 rounded-lg">
                    <span className="text-gray-300">Current Entries</span>
                    <span className="font-bold text-emerald-400">{contest.current_entries || contest.entryCount || 0}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* ROSTER TAB */}
      {activeTab === 'roster' && (
      <div className="max-w-7xl mx-auto px-4 py-6">
        {isLocked ? (
          // Show locked message with your entry
          <div className="text-center py-12">
            <Lock className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Rosters are Locked</h2>
            <p className="text-gray-400 mb-6">
              This contest is now in progress. Check the leaderboard above for live standings!
            </p>
            {existingEntry && (
              <div className="max-w-md mx-auto bg-slate-800/50 rounded-xl p-6 border border-emerald-500/20">
                <h3 className="font-semibold text-white mb-4">Your Entry</h3>
                <div className="space-y-2">
                  {Object.entries(existingEntry.roster || {}).map(([slot, playerId]) => {
                    const player = findPlayerById(playerId as string)
                    return (
                      <div key={slot} className="flex justify-between text-sm">
                        <span className="text-gray-400">{slot}</span>
                        <span className="text-white">{player?.name || 'Unknown'}</span>
                      </div>
                    )
                  })}
                </div>
                {existingEntry.total_points !== undefined && (
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Total Points</span>
                      <span className="text-emerald-400 font-bold">{existingEntry.total_points}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : contest.contest_category === 'survivor' ? (
          // Show SURVIVOR / PICK'EM team picker - Two Column Layout (like DFS roster)
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Your Picks Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-slate-800/50 rounded-xl border border-amber-500/20 sticky top-24">
                <div className="p-4 border-b border-slate-700">
                  <div className="flex items-center justify-between">
                    <h2 className="font-bold text-white flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-amber-400" />
                      Your Picks
                    </h2>
                    <div className="text-right">
                      <div className="text-lg font-bold text-amber-400">
                        {Object.keys(survivorPicks).length}/{(contest.games_data || []).length}
                      </div>
                      <div className="text-xs text-gray-500">picks made</div>
                    </div>
                  </div>
                </div>
                
                {/* Pick Slots */}
                <div className="p-4 space-y-2 max-h-[400px] overflow-y-auto">
                  {(contest.games_data || []).map((game: any, idx: number) => {
                    const gameId = `game_${idx}`
                    const picked = survivorPicks[gameId]
                    const isCaptain = captainPick === gameId
                    
                    return (
                      <div 
                        key={gameId}
                        className={`p-3 rounded-lg border transition-all ${
                          picked 
                            ? isCaptain 
                              ? 'bg-amber-500/10 border-amber-500/50' 
                              : 'bg-emerald-500/10 border-emerald-500/50'
                            : 'bg-slate-900/50 border-slate-700 border-dashed'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-gray-500">Game {idx + 1}</div>
                          {isCaptain && <span className="text-amber-400 text-xs">👑 2x</span>}
                        </div>
                        {picked ? (
                          <div className="flex items-center justify-between mt-1">
                            <span className={`font-bold ${isCaptain ? 'text-amber-400' : 'text-emerald-400'}`}>
                              {picked}
                            </span>
                            {!isLocked && (
                              <button 
                                onClick={() => {
                                  setSurvivorPicks(prev => {
                                    const next = { ...prev }
                                    delete next[gameId]
                                    return next
                                  })
                                  if (isCaptain) setCaptainPick(null)
                                }}
                                className="text-xs text-gray-500 hover:text-red-400"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="text-gray-600 text-sm mt-1">
                            {game.awayTeam} @ {game.homeTeam}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
                
                {/* Submit Button */}
                <div className="p-4 border-t border-slate-700 space-y-3">
                  <input
                    type="text"
                    value={entryName}
                    onChange={(e) => setEntryName(e.target.value)}
                    placeholder="Entry name (optional)"
                    className="w-full px-3 py-2 text-sm bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-500"
                  />
                  
                  {!isLocked ? (
                    <button
                      onClick={async () => {
                        if (!user) {
                          toast.error('Please connect your wallet to submit picks')
                          return
                        }
                        const totalGames = (contest.games_data || []).length
                        const totalPicks = Object.keys(survivorPicks).length
                        if (totalPicks < totalGames) {
                          toast.error(`Please pick a winner for all ${totalGames} games`)
                          return
                        }
                        
                        const pickFormat = String(contest.rosterRules?.pickFormat || '').toLowerCase()
                        const description = String(contest.description || '').toLowerCase()
                        const hasCaptainFormat = pickFormat.includes('captain') || description.includes('captain') || description.includes('2x')
                        if (hasCaptainFormat && !captainPick) {
                          toast.error('Please select one pick as your Captain (2x points)')
                          return
                        }
                        
                        setSubmitting(true)
                        try {
                          await axios.post(`${API_BASE_URL}/api/fantasy/contests/${id}/enter`, {
                            userId: user.id,
                            picks: survivorPicks,
                            captainPick: captainPick,
                            entryName: entryName || `Entry ${myEntries.length + 1}`,
                            contestCategory: 'survivor'
                          })
                          toast.success('Picks submitted!')
                          loadExistingEntry()
                          loadLeaderboard()
                        } catch (err: any) {
                          toast.error(err.response?.data?.error || 'Failed to submit picks')
                        } finally {
                          setSubmitting(false)
                        }
                      }}
                      disabled={submitting || Object.keys(survivorPicks).length < (contest.games_data || []).length}
                      className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-5 h-5" />
                          Submit Picks
                        </>
                      )}
                    </button>
                  ) : (
                    <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-center">
                      <Lock className="w-4 h-4 text-red-400 mx-auto mb-1" />
                      <p className="text-red-400 text-sm font-medium">Locked</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Right Column - Games to Pick From */}
            <div className="lg:col-span-2 space-y-4">
              {/* Header with Rules */}
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                <p className="text-gray-300">
                  {(() => {
                    const winCond = String(contest.rosterRules?.winCondition || '').toLowerCase()
                    const rules = String(contest.rosterRules?.contestRules || '').toLowerCase()
                    if (winCond.includes('stat') || rules.includes('points')) {
                      return '🏈 Pick teams for each game. Your teams\' stats earn you points!'
                    } else if (winCond.includes('yard')) {
                      return '📏 Pick the team with more total yards in each game.'
                    } else if (rules.includes('elimination')) {
                      return '💀 Select a winner for each game. One wrong pick and you\'re eliminated!'
                    } else {
                      return '🎯 Select your picks for each game based on the scoring rules.'
                    }
                  })()}
                </p>
                
                {/* Captain Format Banner */}
                {(() => {
                  const pickFormat = String(contest.rosterRules?.pickFormat || '').toLowerCase()
                  const description = String(contest.description || '').toLowerCase()
                  const hasCaptainFormat = pickFormat.includes('captain') || description.includes('captain') || description.includes('2x')
                  
                  return hasCaptainFormat && (
                    <div className="mt-3 flex items-center gap-2 text-amber-400 bg-amber-500/10 p-2 rounded-lg">
                      <span className="text-xl">👑</span>
                      <span className="text-sm font-medium">Captain Format: Pick one team as Captain for 2x points!</span>
                    </div>
                  )
                })()}
              </div>
              
              {/* Games List */}
              {(contest.games_data || []).map((game: any, idx: number) => {
                const gameId = `game_${idx}`
                const picked = survivorPicks[gameId]
                const isCaptain = captainPick === gameId
                const pickFormat = String(contest.rosterRules?.pickFormat || '').toLowerCase()
                const description = String(contest.description || '').toLowerCase()
                const hasCaptainFormat = pickFormat.includes('captain') || description.includes('captain') || description.includes('2x')
                
                return (
                  <div 
                    key={gameId} 
                    className={`bg-slate-800/50 rounded-xl border p-5 ${
                      isCaptain ? 'border-amber-500 ring-2 ring-amber-500/20' : picked ? 'border-emerald-500/50' : 'border-slate-700'
                    }`}
                  >
                    {/* Game Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-500 bg-slate-700/50 px-2 py-1 rounded">
                          Game {idx + 1}
                        </span>
                        {isCaptain && (
                          <span className="text-xs font-bold text-amber-400 bg-amber-500/20 px-2 py-1 rounded flex items-center gap-1">
                            👑 CAPTAIN (2x)
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {game.gameDate} {game.gameTime && `• ${game.gameTime}`}
                      </div>
                    </div>
                    
                    {/* Team Selection Grid */}
                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Away Team */}
                      <div
                        className={`relative p-4 rounded-xl border-2 transition-all ${
                          picked === game.awayTeam
                            ? isCaptain ? 'border-amber-500 bg-amber-500/10' : 'border-emerald-500 bg-emerald-500/10'
                            : 'border-slate-600 bg-slate-900/50 hover:border-slate-400 hover:bg-slate-800'
                        } ${isLocked ? 'opacity-60' : ''}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="text-xs text-gray-500 mb-1">✈️ Away</div>
                            <div className="text-xl font-bold text-white">{game.awayTeam}</div>
                          </div>
                          {picked === game.awayTeam && (
                            <CheckCircle className={`w-6 h-6 ${isCaptain ? 'text-amber-400' : 'text-emerald-400'}`} />
                          )}
                        </div>
                        
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={() => {
                              setSelectedTeam({ name: game.awayTeam, opponent: game.homeTeam, isHome: false })
                              setSelectedGame(game)
                              setShowTeamModal(true)
                            }}
                            className="flex-1 py-2 px-3 text-xs bg-slate-700 text-blue-400 rounded-lg hover:bg-slate-600 transition-colors"
                          >
                            📊 Stats
                          </button>
                          <button
                            onClick={() => !isLocked && setSurvivorPicks(prev => ({ ...prev, [gameId]: game.awayTeam }))}
                            disabled={isLocked}
                            className={`flex-1 py-2 px-3 text-xs font-medium rounded-lg transition-all ${
                              picked === game.awayTeam
                                ? isCaptain ? 'bg-amber-500 text-white' : 'bg-emerald-500 text-white'
                                : 'bg-slate-600 text-white hover:bg-emerald-600'
                            }`}
                          >
                            {picked === game.awayTeam ? '✓ Picked' : 'Pick'}
                          </button>
                        </div>
                      </div>
                      
                      {/* Home Team */}
                      <div
                        className={`relative p-4 rounded-xl border-2 transition-all ${
                          picked === game.homeTeam
                            ? isCaptain ? 'border-amber-500 bg-amber-500/10' : 'border-emerald-500 bg-emerald-500/10'
                            : 'border-slate-600 bg-slate-900/50 hover:border-slate-400 hover:bg-slate-800'
                        } ${isLocked ? 'opacity-60' : ''}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="text-xs text-gray-500 mb-1">🏠 Home</div>
                            <div className="text-xl font-bold text-white">{game.homeTeam}</div>
                          </div>
                          {picked === game.homeTeam && (
                            <CheckCircle className={`w-6 h-6 ${isCaptain ? 'text-amber-400' : 'text-emerald-400'}`} />
                          )}
                        </div>
                        
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={() => {
                              setSelectedTeam({ name: game.homeTeam, opponent: game.awayTeam, isHome: true })
                              setSelectedGame(game)
                              setShowTeamModal(true)
                            }}
                            className="flex-1 py-2 px-3 text-xs bg-slate-700 text-blue-400 rounded-lg hover:bg-slate-600 transition-colors"
                          >
                            📊 Stats
                          </button>
                          <button
                            onClick={() => !isLocked && setSurvivorPicks(prev => ({ ...prev, [gameId]: game.homeTeam }))}
                            disabled={isLocked}
                            className={`flex-1 py-2 px-3 text-xs font-medium rounded-lg transition-all ${
                              picked === game.homeTeam
                                ? isCaptain ? 'bg-amber-500 text-white' : 'bg-emerald-500 text-white'
                                : 'bg-slate-600 text-white hover:bg-emerald-600'
                            }`}
                          >
                            {picked === game.homeTeam ? '✓ Picked' : 'Pick'}
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Captain Selection - Only if captain format and a team is picked */}
                    {hasCaptainFormat && picked && !isLocked && (
                      <div className="mt-4 pt-4 border-t border-slate-700 text-center">
                        <button
                          onClick={() => setCaptainPick(isCaptain ? null : gameId)}
                          className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                            isCaptain
                              ? 'bg-amber-500 text-white'
                              : 'bg-slate-700 text-amber-400 hover:bg-amber-500/20 border border-amber-500/50'
                          }`}
                        >
                          {isCaptain ? '✓ Captain Selected (2x Points)' : '👑 Make This My Captain (2x Points)'}
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ) : (
          // Show DFS roster builder
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Your Roster */}
            <div className="lg:col-span-1">
              <div className="bg-slate-800/50 rounded-xl border border-emerald-500/20 sticky top-24">
                <div className="p-4 border-b border-slate-700">
                  <div className="flex items-center justify-between">
                    <h2 className="font-bold text-white flex items-center gap-2">
                      <UserPlus className="w-5 h-5 text-emerald-400" />
                      Your Roster
                    </h2>
                    
                    {/* Total Points & Last Update */}
                    {existingEntry && (
                      <div className="text-right">
                        <div className="text-lg font-bold text-emerald-400">
                          {(existingEntry.total_points || 0).toFixed(1)} pts
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          {isRefreshingScores ? (
                            <Loader className="w-3 h-3 animate-spin" />
                          ) : (
                            <Clock className="w-3 h-3" />
                          )}
                          {lastScoreUpdate ? (
                            <span>Updated {lastScoreUpdate.toLocaleTimeString()}</span>
                          ) : (
                            <span>Live scores</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Refresh Scores Button */}
                  {existingEntry && isLocked && (
                    <button
                      onClick={refreshScores}
                      disabled={isRefreshingScores}
                      className="mt-2 w-full py-2 bg-amber-500/20 text-amber-400 rounded-lg text-sm font-medium hover:bg-amber-500/30 transition-all flex items-center justify-center gap-2"
                    >
                      {isRefreshingScores ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          Refreshing...
                        </>
                      ) : (
                        <>
                          <Zap className="w-4 h-4" />
                          Refresh Live Scores
                        </>
                      )}
                    </button>
                  )}
                  
                  {/* Salary Cap */}
                  {contest.rosterRules?.salaryCap && (
                    <div className="mt-3">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-400">Salary Cap</span>
                        <span className={`font-medium ${salaryRemaining < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                          ${salaryRemaining.toLocaleString()} remaining
                        </span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-2">
                        <div 
                          className={`h-full rounded-full transition-all ${
                            salaryRemaining < 0 ? 'bg-red-500' : 'bg-emerald-500'
                          }`}
                          style={{ width: `${Math.min(100, (salaryUsed / salaryCap) * 100)}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>${salaryUsed.toLocaleString()} used</span>
                        <span>${salaryCap.toLocaleString()} cap</span>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Roster Slots */}
                <div className="p-4 space-y-2 max-h-[500px] overflow-y-auto">
                  {rosterSlots.map((slot, index) => {
                    const slotKey = rosterSlots.filter(s => s.position === slot.position).length > 1
                      ? `${slot.position}${slot.slotIndex + 1}`
                      : slot.position
                    
                    return (
                      <div
                        key={`${slot.position}-${slot.slotIndex}`}
                        className={`p-3 rounded-lg border transition-all ${
                          slot.player
                            ? 'bg-emerald-500/10 border-emerald-500/30'
                            : expandedPosition === slotKey
                              ? 'bg-slate-700/50 border-emerald-500/50'
                              : 'bg-slate-900/30 border-slate-700/50 hover:border-slate-600'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="w-10 text-center text-xs font-bold text-emerald-400 bg-emerald-500/20 px-2 py-1 rounded">
                              {slot.position}
                            </span>
                            {slot.player ? (
                              <div className="flex-1">
                                <div 
                                  className="font-medium text-white hover:text-emerald-400 cursor-pointer transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setSelectedPlayer(slot.player)
                                    setShowPlayerModal(true)
                                  }}
                                >
                                  {slot.player.name}
                                </div>
                                <div className="text-xs text-gray-400">
                                  {slot.player.team} • ${slot.player.salary?.toLocaleString()}
                                </div>
                              </div>
                            ) : (
                              <button
                                onClick={() => setExpandedPosition(expandedPosition === slotKey ? null : slotKey)}
                                className="text-gray-400 hover:text-white transition-colors"
                              >
                                + Select {slot.position}
                              </button>
                            )}
                          </div>
                          
                          {slot.player ? (
                            <div className="flex items-center gap-2">
                              {/* Player Score */}
                              {(() => {
                                const playerScore = getPlayerScore(slot.player.id)
                                if (playerScore && playerScore.points > 0) {
                                  return (
                                    <div className="text-right">
                                      <div className="text-sm font-bold text-emerald-400">
                                        {playerScore.points.toFixed(1)}
                                      </div>
                                      <div className="text-[10px] text-gray-500">pts</div>
                                    </div>
                                  )
                                }
                                return null
                              })()}
                              
                              {/* Remove button - only show if not locked */}
                              {!isLocked && (
                                <button
                                  onClick={() => removePlayer(slotKey)}
                                  className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-all"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          ) : (
                            <button
                              onClick={() => setExpandedPosition(expandedPosition === slotKey ? null : slotKey)}
                              className="text-gray-500"
                            >
                              {expandedPosition === slotKey ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </button>
                          )}
                        </div>
                        
                        {/* Score breakdown for filled slot */}
                        {slot.player && (() => {
                          const playerScore = getPlayerScore(slot.player.id)
                          if (playerScore && Object.keys(playerScore.breakdown).length > 0) {
                            return (
                              <div className="mt-2 pt-2 border-t border-slate-700/50">
                                <div className="grid grid-cols-2 gap-1">
                                  {Object.entries(playerScore.breakdown)
                                    .filter(([_, pts]) => pts !== 0)
                                    .map(([stat, pts]) => (
                                      <div key={stat} className="flex justify-between text-[10px]">
                                        <span className="text-gray-500 truncate">{stat}</span>
                                        <span className={pts > 0 ? 'text-emerald-400' : 'text-red-400'}>
                                          {pts > 0 ? '+' : ''}{(pts as number).toFixed(1)}
                                        </span>
                                      </div>
                                    ))}
                                </div>
                              </div>
                            )
                          }
                          return null
                        })()}
                        
                        {/* Expanded player selection */}
                        <AnimatePresence>
                          {expandedPosition === slotKey && !slot.player && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="mt-3 pt-3 border-t border-slate-700 space-y-2 max-h-48 overflow-y-auto">
                                {availablePlayers
                                  .filter(p => {
                                    const slotPos = (slot.position || '').toUpperCase()
                                    const playerPos = (p.position || '').toUpperCase()
                                    const fantasyPositions = ['QB', 'RB', 'WR', 'TE', 'K']
                                    
                                    // 🎯 AGENTIC: Check if AI defined eligiblePositions for this slot
                                    const slotConfig = contest.rosterRules?.positions?.[slot.position] || 
                                                       contest.rosterRules?.positions?.[slotPos]
                                    // Check if it's the new format (object with eligiblePositions) vs old format (just a number)
                                    if (slotConfig && typeof slotConfig === 'object' && 'eligiblePositions' in slotConfig && slotConfig.eligiblePositions) {
                                      // AI defined exactly which positions can fill this slot - use that!
                                      const eligible = slotConfig.eligiblePositions.map((pos: string) => pos.toUpperCase())
                                      return eligible.includes(playerPos)
                                    }
                                    
                                    // FALLBACK for legacy contests without eligiblePositions:
                                    const knownSlotTypes = ['QB', 'RB', 'WR', 'TE', 'K', 'FLEX', 'SUPERFLEX', 'SF']
                                    
                                    // Direct position match
                                    if (slotPos === playerPos) return true
                                    
                                    // FLEX can take RB, WR, TE
                                    if (slotPos === 'FLEX' && ['RB', 'WR', 'TE'].includes(playerPos)) return true
                                    
                                    // SUPERFLEX/SF can take QB, RB, WR, TE
                                    if ((slotPos === 'SUPERFLEX' || slotPos === 'SF') && ['QB', 'RB', 'WR', 'TE'].includes(playerPos)) return true
                                    
                                    // CPT/CAPTAIN can take ANY fantasy position
                                    if ((slotPos === 'CPT' || slotPos === 'CAPTAIN') && fantasyPositions.includes(playerPos)) return true
                                    
                                    // MVP can take ANY fantasy position
                                    if (slotPos === 'MVP' && fantasyPositions.includes(playerPos)) return true
                                    
                                    // Unknown slot types - allow any fantasy position
                                    if (!knownSlotTypes.includes(slotPos) && fantasyPositions.includes(playerPos)) return true
                                    
                                    return false
                                  })
                                  .slice(0, 15)
                                  .map(player => {
                                    const playerCanAfford = !salaryCap || salaryCap === 0 || salaryRemaining >= (player.salary || 0)
                                    return (
                                      <button
                                        key={player.id}
                                        onClick={() => playerCanAfford && addPlayer(player, slotKey)}
                                        disabled={!playerCanAfford}
                                        className={`w-full p-2 rounded-lg text-left transition-colors ${
                                          playerCanAfford 
                                            ? 'bg-slate-800/50 hover:bg-slate-700/50' 
                                            : 'bg-slate-900/30 opacity-50 cursor-not-allowed'
                                        }`}
                                      >
                                        <div className="flex justify-between items-center">
                                          <div>
                                            <div className="text-sm font-medium text-white">{player.name}</div>
                                            <div className="text-xs text-gray-400">{player.team} • {player.position}</div>
                                          </div>
                                          <div className="text-right">
                                            <div className={`text-sm ${playerCanAfford ? 'text-emerald-400' : 'text-red-400'}`}>
                                              ${player.salary?.toLocaleString()}
                                            </div>
                                            {!playerCanAfford && (
                                              <div className="text-xs text-red-400">Over budget</div>
                                            )}
                                          </div>
                                        </div>
                                      </button>
                                    )
                                  })}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )
                  })}
                </div>
                
                {/* Entry Name & Multi-Entry Management */}
                <div className="p-4 border-t border-slate-700 space-y-4">
                  {/* Entry Name Input */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">
                      Entry Name (shown on leaderboard)
                    </label>
                    <input
                      type="text"
                      value={entryName}
                      onChange={(e) => setEntryName(e.target.value)}
                      placeholder={`Entry #${myEntries.length + 1}`}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/30"
                    />
                  </div>
                  
                  {/* My Entries (if user has any) */}
                  {myEntries.length > 0 && (
                    <div className="bg-slate-800/50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-400">
                          Your Entries ({myEntries.length}/{maxEntries})
                        </span>
                        {myEntries.length < maxEntries && (
                          <button
                            onClick={startNewEntry}
                            className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
                          >
                            <UserPlus className="w-3 h-3" />
                            Add Entry
                          </button>
                        )}
                      </div>
                      <div className="space-y-1">
                        {myEntries.map((entry) => (
                          <button
                            key={entry.id}
                            onClick={() => switchEntry(entry)}
                            className={`w-full px-3 py-2 rounded text-left text-sm transition-colors ${
                              currentEntryId === entry.id
                                ? 'bg-emerald-600/20 border border-emerald-500/50 text-emerald-300'
                                : 'bg-slate-700/50 hover:bg-slate-700 text-gray-300'
                            }`}
                          >
                            <div className="flex justify-between items-center">
                              <span>{entry.entry_name || `Entry #${entry.entry_number}`}</span>
                              {entry.total_points > 0 && (
                                <span className="text-xs text-yellow-400">{entry.total_points.toFixed(1)} pts</span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Entry Fee Info */}
                  {contest.entry_fee > 0 && !existingEntry && (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-center">
                      <span className="text-sm text-yellow-300">
                        Entry fee: ${contest.entry_fee}
                      </span>
                    </div>
                  )}
                  
                  {/* Submit Button */}
                  <button
                    onClick={submitRoster}
                    disabled={!isRosterComplete || submitting || !user}
                    className={`w-full py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                      isRosterComplete && user
                        ? 'bg-gradient-to-r from-emerald-600 to-green-600 text-white hover:from-emerald-500 hover:to-green-500'
                        : 'bg-slate-700 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {submitting ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin" />
                        Submitting...
                      </>
                    ) : !user ? (
                      <>
                        <Lock className="w-5 h-5" />
                        Connect Wallet to Enter
                      </>
                    ) : existingEntry ? (
                      <>
                        <CheckCircle className="w-5 h-5" />
                        Update {entryName || 'Entry'}
                      </>
                    ) : myEntries.length > 0 ? (
                      <>
                        <UserPlus className="w-5 h-5" />
                        Add New Entry ({myEntries.length + 1}/{maxEntries})
                      </>
                    ) : (
                      <>
                        <Zap className="w-5 h-5" />
                        Submit Entry
                      </>
                    )}
                  </button>
                  
                  {!isRosterComplete && user && (
                    <p className="text-center text-xs text-gray-500">
                      Fill all roster slots to submit
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Right Column - Player Pool */}
            <div className="lg:col-span-2">
              <div className="bg-slate-800/50 rounded-xl border border-slate-700/50">
                {/* Filters */}
                <div className="p-4 border-b border-slate-700">
                  <div className="flex flex-wrap items-center gap-3">
                    {/* Search */}
                    <div className="flex-1 relative min-w-[200px]">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <input
                        type="text"
                        placeholder="Search players..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-emerald-500"
                      />
                    </div>
                    
                    {/* Position Filter */}
                    <div className="flex items-center gap-1 flex-wrap">
                      {positions.map(pos => (
                        <button
                          key={pos}
                          onClick={() => setPositionFilter(pos)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                            positionFilter === pos
                              ? 'bg-emerald-500 text-white'
                              : 'bg-slate-700/50 text-gray-400 hover:bg-slate-700'
                          }`}
                        >
                          {pos}
                        </button>
                      ))}
                    </div>
                    
                    {/* Sort */}
                    <select
                      value={sortBy}
                      onChange={e => setSortBy(e.target.value as any)}
                      className="px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white text-sm"
                    >
                      <option value="salary">Salary (High to Low)</option>
                      <option value="projected">Projected Points</option>
                      <option value="name">Name (A-Z)</option>
                    </select>
                  </div>
                </div>
                
                {/* Player Grid */}
                <div className="p-4">
                  {availablePlayers.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-400">No players match your filters</p>
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-3">
                      {availablePlayers.map(player => (
                        <PlayerCard
                          key={player.id}
                          player={player}
                          onSelect={(slotKey) => addPlayer(player, slotKey)}
                          onShowInsights={(p) => {
                            setSelectedPlayer(p)
                            setShowPlayerModal(true)
                          }}
                          rosterSlots={rosterSlots}
                          salaryRemaining={salaryRemaining}
                          salaryCap={contest.rosterRules?.salaryCap}
                          slotConfigs={contest.rosterRules?.positions}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      )}
      
      {/* Player Insights Modal */}
      <PlayerInsightsModal
        isOpen={showPlayerModal}
        onClose={() => {
          setShowPlayerModal(false)
          setSelectedPlayer(null)
        }}
        player={selectedPlayer}
        contestId={id || ''}
        userId={user?.id}
        username={user?.username}
        scoringRules={contest?.scoringSystem?.rules}
      />
      
      {/* Team Insights Modal (for survivor contests) */}
      <TeamInsightsModal
        isOpen={showTeamModal}
        onClose={() => {
          setShowTeamModal(false)
          setSelectedTeam(null)
          setSelectedGame(null)
        }}
        team={selectedTeam}
        game={selectedGame}
        contestId={id || ''}
        userId={user?.id}
        username={user?.username}
        onPickTeam={(teamName) => {
          // Find the game index for this team
          const gameIdx = (contest?.games_data || []).findIndex(
            (g: any) => g.homeTeam === teamName || g.awayTeam === teamName
          )
          if (gameIdx >= 0) {
            setSurvivorPicks(prev => ({ ...prev, [`game_${gameIdx}`]: teamName }))
          }
        }}
        isPicked={selectedTeam ? Object.values(survivorPicks).includes(selectedTeam.name) : false}
      />
    </div>
  )
}

// ============================================================================
// PLAYER CARD COMPONENT
// ============================================================================

interface PlayerCardProps {
  player: Player
  onSelect: (slotKey: string) => void
  onShowInsights: (player: Player) => void
  rosterSlots: RosterSlot[]
  salaryRemaining: number
  salaryCap?: number
  slotConfigs?: Record<string, number | { count?: number, eligiblePositions?: string[], multiplier?: number, description?: string }>
}

function PlayerCard({ player, onSelect, onShowInsights, rosterSlots, salaryRemaining, salaryCap, slotConfigs }: PlayerCardProps) {
  const [showSlots, setShowSlots] = useState(false)
  
  // Find available slots for this player - reads from AI-defined eligiblePositions first!
  const availableSlots = useMemo(() => {
    const playerPos = (player.position || '').toUpperCase()
    const fantasyPositions = ['QB', 'RB', 'WR', 'TE', 'K']
    
    return rosterSlots.filter(slot => {
      if (slot.player) return false // Already filled
      
      const slotPos = (slot.position || '').toUpperCase()
      
      // 🎯 AGENTIC: Check if AI defined eligiblePositions for this slot
      const slotConfig = slotConfigs?.[slot.position] || slotConfigs?.[slotPos]
      // Check if it's the new format (object with eligiblePositions) vs old format (just a number)
      if (slotConfig && typeof slotConfig === 'object' && 'eligiblePositions' in slotConfig && slotConfig.eligiblePositions) {
        const eligible = slotConfig.eligiblePositions.map((pos: string) => pos.toUpperCase())
        return eligible.includes(playerPos)
      }
      
      // FALLBACK for legacy contests without eligiblePositions:
      const knownSlotTypes = ['QB', 'RB', 'WR', 'TE', 'K', 'FLEX', 'SUPERFLEX', 'SF']
      
      // Direct position match
      if (slotPos === playerPos) return true
      
      // FLEX can take RB, WR, TE
      if (slotPos === 'FLEX' && ['RB', 'WR', 'TE'].includes(playerPos)) return true
      
      // SUPERFLEX/SF can take QB, RB, WR, TE
      if ((slotPos === 'SUPERFLEX' || slotPos === 'SF') && ['QB', 'RB', 'WR', 'TE'].includes(playerPos)) return true
      
      // MVP can take ANY fantasy position
      if (slotPos === 'MVP' && fantasyPositions.includes(playerPos)) return true
      
      // CPT/CAPTAIN can take ANY fantasy position
      if ((slotPos === 'CPT' || slotPos === 'CAPTAIN') && fantasyPositions.includes(playerPos)) return true
      
      // Unknown slot types - allow any fantasy position
      if (!knownSlotTypes.includes(slotPos) && fantasyPositions.includes(playerPos)) return true
      
      return false
    })
  }, [player.position, rosterSlots, slotConfigs])
  
  const canAfford = !salaryCap || salaryCap === 0 || (salaryRemaining >= (player.salary || 0))
  
  const handleClick = () => {
    console.log('🖱️ PlayerCard clicked:', player.name, 'Position:', player.position)
    console.log('   Available slots:', availableSlots.length, availableSlots.map(s => s.position))
    console.log('   Can afford:', canAfford, '| Salary:', player.salary, '| Remaining:', salaryRemaining)
    
    if (!canAfford) {
      toast.error('Not enough salary cap!')
      return
    }
    
    if (availableSlots.length === 0) {
      toast.error(`No available slots for ${player.position}`)
      return
    }
    
    if (availableSlots.length === 1) {
      // Auto-select if only one slot
      const slot = availableSlots[0]
      const samePositionSlots = rosterSlots.filter(s => s.position === slot.position)
      const slotKey = samePositionSlots.length > 1
        ? `${slot.position}${slot.slotIndex + 1}`
        : slot.position
      
      console.log('   Auto-selecting slot:', slotKey)
      onSelect(slotKey)
      setShowSlots(false)
    } else {
      // Show slot selection dropdown
      console.log('   Showing slot selection dropdown')
      setShowSlots(!showSlots)
    }
  }
  
  return (
    <div className={`p-4 rounded-lg border transition-all ${
      canAfford
        ? 'bg-slate-900/30 border-slate-700/50 hover:border-emerald-500/50'
        : 'bg-slate-900/20 border-slate-800/50 opacity-60'
    }`}>
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold text-emerald-400 bg-emerald-500/20 px-2 py-0.5 rounded">
              {player.position}
            </span>
            <span className="text-xs text-gray-500">{player.team}</span>
            {player.opponent && (
              <span className="text-xs text-gray-600">vs {player.opponent}</span>
            )}
          </div>
          <h3 
            className="font-semibold text-white hover:text-emerald-400 cursor-pointer transition-colors"
            onClick={(e) => {
              e.stopPropagation()
              onShowInsights(player)
            }}
          >
            {player.name}
          </h3>
          {player.injury && (
            <span className="text-xs text-red-400">{player.injury}</span>
          )}
        </div>
        
        <div className="text-right">
          <div className="text-lg font-bold text-emerald-400">
            ${player.salary?.toLocaleString()}
          </div>
          {player.projectedPoints && (
            <div className="text-xs text-gray-500">
              {player.projectedPoints} pts proj
            </div>
          )}
        </div>
      </div>
      
      <div className="mt-3 space-y-2">
        <div className="flex gap-2">
          <button
            onClick={handleClick}
            disabled={!canAfford || availableSlots.length === 0}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
              canAfford && availableSlots.length > 0
                ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border border-emerald-500/30'
                : 'bg-slate-800 text-gray-600 cursor-not-allowed'
            }`}
          >
            {!canAfford ? 'Over Budget' : availableSlots.length === 0 ? 'No Slots' : 'Add to Roster'}
          </button>
          
          {/* Intel Button */}
          <button
            onClick={() => onShowInsights(player)}
            className="px-3 py-2 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-lg text-sm font-medium transition-all border border-blue-500/30"
            title="Research & Intel"
          >
            🔍
          </button>
        </div>
        
        {/* Slot selection dropdown */}
        <AnimatePresence>
          {showSlots && availableSlots.length > 1 && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="mt-2 space-y-1">
                {availableSlots.map((slot, idx) => {
                  const slotKey = rosterSlots.filter(s => s.position === slot.position).length > 1
                    ? `${slot.position}${slot.slotIndex + 1}`
                    : slot.position
                  return (
                    <button
                      key={idx}
                      onClick={() => {
                        onSelect(slotKey)
                        setShowSlots(false)
                      }}
                      className="w-full py-2 px-3 bg-slate-800/50 hover:bg-slate-700/50 rounded text-sm text-gray-300 text-left"
                    >
                      Add to {slotKey}
                    </button>
                  )
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

